import { useRef,  useState , memo , useCallback} from 'react';
import Map from '../components/map/Map.js'
import Grid from '@mui/material/Grid'
import {useFetchStation} from '../hooks/useFetchStation'
import {useFetchRouting} from '../hooks/useFetchRouting';
import {Marker, Tooltip, Polyline} from 'react-leaflet'
import Header from "../components/Header";
import MapCardStation from '../components/map/MapCardStation.js'
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {useSharedLang} from '../hooks/useSharedLang.js';
import {LANGS , geoPositionOptions} from "../components/constants.js";
import {useGeolocated} from '../hooks/useGeolocated';
import {useDikstra} from '../hooks/useDikstra';
import Loading from "../components/Loading.js";
import {getIcon, getTooltipOptions} from "../utils/UrlUtil.js";

/**
 * 2025/06/29 駅/食事を施設と乗り物に分離
 */
const Station = memo(() => {
    const mapRef = useRef(null);

    const {stationList, isLoading} = useFetchStation();
    const {routingList, isRouteLoading} = useFetchRouting();
    const [inRouteGuid, setInRouteGuid] = useState(false);
    const selectedMarkerKey = useRef(null);
    
    const defaultSelectedStation = useRef(null);
    const isUseDefaultSelectedStation = useRef(true);
    const activeStationList = useRef(null);

    const { id } = useParams();
    const { t } = useTranslation();
    const { sharedLang } = useSharedLang();

    /**
     * リアルタイム座標チェック
     * 
     * @param {*} pos 
     */
    const setCurrentPosition = (pos) => {
        //現在地をマップに表示
        if(mapRef.current != null){
            mapRef.current.updateMarker(pos.coords.latitude, pos.coords.longitude);
            lastCoords.current =  pos;
        }
    };

    //--- 現在地 ---
    //useGeolocated の coordsは使わない。使うと座標が更新されるたびに画面が再描写してしまう。
    const { isGeolocationAvailable, isGeolocationEnabled  } = useGeolocated({
        ...geoPositionOptions , 
        onSuccess : (pos) => setCurrentPosition(pos),
    });
    const lastCoords = useRef(null);


    const { routing, getNearNode } = useDikstra({routingData : routingList})

    const [selectedStation , setSelectedStation] = useState(null);

    const getMarkerKey = useCallback((station , coordinate) => {
        return station.id + '-' + coordinate[0] + '-' + coordinate[1];
    },[]);

    const parseMarkerKey = useCallback((markerKey) => {
        const [id, latitude, longitude] = markerKey.split('-');
        return {id:id , longitude:longitude, latitude:latitude}
    },[]);

    const toggleRouteGuid = (flg) => {
        setInRouteGuid(flg);
    };

    const clickMarker = (station, coordinate) => {
        selectedMarkerKey.current = getMarkerKey(station, coordinate);
        setSelectedStation(station);
    };

    const resetMarker = useCallback(() => {
        selectedMarkerKey.current = null;
        isUseDefaultSelectedStation.current = false;
        setSelectedStation(null);
        setInRouteGuid(false);
    },[]);
    
    const selectStation = useCallback((station) => {
        selectedMarkerKey.current = null;
        setSelectedStation(station);
    },[]);

    const isActiveMarker = (station) => {
        let currentElement = getActiveElement();    
        return !currentElement || currentElement.id === station.id;
    };
    
    const isStation = useCallback((station) => {
        if(!station){
            return false;
        }
        return station.category === 'station';
    },[]);

    const getActiveElement = useCallback(() => {
        let ret = selectedStation;
        if(selectedStation == null && isUseDefaultSelectedStation.current){
            ret = defaultSelectedStation.current;
        }
        return ret;
    },[selectedStation]);

    const getPolyline = useCallback(() => {
        let selectedMarker = parseMarkerKey(selectedMarkerKey.current);
        if(lastCoords.current == null || selectedMarker == null){
            return [];
        }

        let routList =  routing(
            getNearNode(lastCoords.current.coords),
            getNearNode({
                latitude: selectedMarker.latitude,
                longitude: selectedMarker.longitude
            })
        );
        let ret = [];
        for (let i = 0; i < routList.length; i++) {
            ret[i] = [routList[i].latitude, routList[i].longitude];
        }
        return ret;
    },[selectedStation, lastCoords.current]);

    const getActiveStation = () => {
        let elements = [];
        if(isLoading){
          return elements;
        }
        for (let index = 0; index < stationList.length; index++) {
            let station = stationList[index];
            if(sharedLang === LANGS.ENGLISH){
                station.name = station.nameEnglish;
                station.lineName = station.lineNameEnglish;
                station.description = station.descriptionEnglish;
                if(station.category === 'restaurant'){
                    station.open = station.openEnglish;
                    station.shopInfo = station.shopInfoEnglish;
                }
            }
            elements.push(station);
        }
        return elements;
    }

    if (isLoading || isRouteLoading) {
        return (<Loading></Loading>);
    }
    activeStationList.current = getActiveStation();

    if(id && defaultSelectedStation.current == null){
        activeStationList.current.forEach((element) =>{
            if(String(element.id) === id){
                defaultSelectedStation.current = element;
                selectStation(element);
                selectedMarkerKey.current = getMarkerKey(
                    element,
                    [element.positions[0].latitude , element.positions[0].longitude]
                );
            }
        })
    }

    return (
        <>
        <Header 
          display={true} 
          title={t("station.title")} 
          displayList={false}
        />
        <Grid container justifyContent="center" alignItems="center" direction="column">
        <Map ref={mapRef}>
        {
            activeStationList.current.map((element, fIndex) =>
                element.positions.map((position, pIndex) => {
                    let coordinate = [position.latitude, position.longitude];
                    let markerKey = getMarkerKey(element, coordinate);

                    return <Marker 
                        key={markerKey} 
                        position={coordinate}
                        icon={getIcon(element.markerIcon)}
                        eventHandlers={{
                            click: (e) => {
                              clickMarker(element, coordinate);
                            }
                          }}
                        >
                        {
                        isActiveMarker(element) &&
                        <Tooltip
                            {...getTooltipOptions(
                                fIndex + pIndex , 
                                coordinate,
                                element.category
                            )}
                        >
                            {element.name}
                        </Tooltip>
                    }
                    </Marker>
                })
            )
        }
         <div>{inRouteGuid && <Polyline positions={getPolyline()} />}</div>
        </Map>
        <MapCardStation 
            data={getActiveElement()} 
            resetMarker={resetMarker} 
            isRouteEnabled={isGeolocationAvailable && isGeolocationEnabled && lastCoords.current != null}
            inRouteGuid = {inRouteGuid}
            toggleRouteGuid = {toggleRouteGuid}            
            />     
        </Grid>
        </>
    )
});
export default Station